import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { CertificateFieldMapping, CertificateFieldType } from '@/types/certificate.types';
import { useGetProcessById, useGetFormById } from '@/hooks';
import { Loader2, Bold, Italic, Underline } from 'lucide-react';

interface FieldConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  mapping: CertificateFieldMapping | null;
  processId: string;
  onSave: (updates: Partial<CertificateFieldMapping>) => Promise<void>;
}

const FONT_FAMILIES = [
  'Helvetica',
  'Times-Roman',
  'Courier',
  'Arial',
  'Verdana',
  'Georgia',
  'Palatino',
];

const getAlignments = (t: any) => [
  { value: 'left', label: t('certificate.fieldConfiguration.alignments.left') },
  { value: 'center', label: t('certificate.fieldConfiguration.alignments.center') },
  { value: 'right', label: t('certificate.fieldConfiguration.alignments.right') },
];

export const FieldConfigurationModal: React.FC<FieldConfigurationModalProps> = ({
  isOpen,
  onClose,
  mapping,
  processId,
  onSave,
}) => {
  const { t } = useTranslation();
  const [fieldConfig, setFieldConfig] = useState({
    fontSize: mapping?.fontSize || 16,
    fontFamily: mapping?.fontFamily || 'Helvetica',
    color: mapping?.color || '#000000',
    alignment: mapping?.alignment || 'left',
    label: mapping?.label || '',
    sourceFormId: mapping?.sourceFormId || '',
    sourceQuestionId: mapping?.sourceQuestionId || '',
    userProfileField: mapping?.userProfileField || '',
    isBold: mapping?.isBold ?? false,
    isItalic: mapping?.isItalic ?? false,
    isUnderlined: mapping?.isUnderlined ?? false,
    textTransform: mapping?.textTransform || 'none',
    opacity: mapping?.opacity ?? 1,
    shrinkToFit: mapping?.shrinkToFit ?? false,
  });
  const [saving, setSaving] = useState(false);

  const { data: processData } = useGetProcessById(processId);
  const { data: selectedFormData } = useGetFormById(fieldConfig.sourceFormId || '');

  const [forms, setForms] = useState<Array<{ id: string; name: string }>>([]);
  const [questions, setQuestions] = useState<Array<{ id: string; label: string }>>([]);

  useEffect(() => {
    if (mapping) {
      setFieldConfig({
        fontSize: mapping.fontSize || 16,
        fontFamily: mapping.fontFamily || 'Helvetica',
        color: mapping.color || '#000000',
        alignment: mapping.alignment || 'left',
        label: mapping.label || '',
        sourceFormId: mapping.sourceFormId || '',
        sourceQuestionId: mapping.sourceQuestionId || '',
        userProfileField: mapping.userProfileField || '',
        isBold: mapping.isBold ?? false,
        isItalic: mapping.isItalic ?? false,
        isUnderlined: mapping.isUnderlined ?? false,
        textTransform: mapping.textTransform || 'none',
        opacity: mapping.opacity ?? 1,
        shrinkToFit: mapping.shrinkToFit ?? false,
      });
    }
  }, [mapping]);

  useEffect(() => {
    if (processData?.data?.forms) {
      const processForms = processData.data.forms.map((pf: any) => ({
        id: pf.formId || pf.form?.id,
        name: pf.form?.name || 'Unknown Form',
      }));
      setForms(processForms);
    }
  }, [processData]);

  useEffect(() => {
    if (selectedFormData?.design && fieldConfig.sourceFormId) {
      const extractedQuestions: Array<{ id: string; label: string }> = [];
      
      const extractQuestions = (sections: any[]) => {
        sections.forEach((section) => {
          if (section.questions) {
            section.questions.forEach((question: any) => {
              if (question.id && question.label) {
                extractedQuestions.push({
                  id: question.id,
                  label: question.label,
                });
              }
            });
          }
        });
      };

      if (Array.isArray(selectedFormData.design)) {
        extractQuestions(selectedFormData.design);
      }

      setQuestions(extractedQuestions);
    } else {
      setQuestions([]);
    }
  }, [selectedFormData, fieldConfig.sourceFormId]);

  const handleSave = async () => {
    if (!mapping) return;

    try {
      setSaving(true);
      
      // If changing to photo field, adjust dimensions to maintain 3:4 aspect ratio (passport standard)
      const updates: any = {
        fontSize: fieldConfig.fontSize,
        fontFamily: fieldConfig.fontFamily,
        color: fieldConfig.color,
        alignment: fieldConfig.alignment,
        label: fieldConfig.label,
        sourceFormId: fieldConfig.sourceFormId || null,
        sourceQuestionId: fieldConfig.sourceQuestionId || null,
        userProfileField: fieldConfig.userProfileField || null,
        isBold: fieldConfig.isBold,
        isItalic: fieldConfig.isItalic,
        isUnderlined: fieldConfig.isUnderlined,
        textTransform: fieldConfig.textTransform !== 'none' ? fieldConfig.textTransform : null,
        opacity: fieldConfig.opacity,
        shrinkToFit: fieldConfig.shrinkToFit,
      };
      
      // If changing to photo, adjust dimensions to 7:9 ratio (ICAO passport photo standard)
      if (fieldConfig.userProfileField === 'photo' && mapping.userProfileField !== 'photo') {
        const currentWidth = mapping.width || 100;
        const currentHeight = mapping.height || 100;
        
        // Calculate new dimensions maintaining 7:9 ratio (width:height = 7:9)
        // Use the larger dimension to determine the size
        if (currentWidth > currentHeight) {
          // Width is larger - adjust height to maintain 7:9 ratio
          updates.width = currentWidth;
          updates.height = currentWidth * (9 / 7); // height = width * 9/7 for 7:9 ratio
        } else {
          // Height is larger - adjust width to maintain 7:9 ratio
          updates.height = currentHeight;
          updates.width = currentHeight * (7 / 9); // width = height * 7/9 for 7:9 ratio
        }
      }
      
      await onSave(updates);
      onClose();
    } catch (error) {
      console.error('Error saving field configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!mapping) return null;

  const isCustomField = mapping.fieldType === 'CUSTOM';
  const isUserProfileField = mapping.fieldType === 'USER_PROFILE';

  const getUserProfileFieldOptions = (t: any) => [
    { value: 'firstName', label: t('certificate.fieldConfiguration.userProfileFields.firstName') },
    { value: 'lastName', label: t('certificate.fieldConfiguration.userProfileFields.lastName') },
    { value: 'fullName', label: t('certificate.fieldConfiguration.userProfileFields.fullName') },
    { value: 'email', label: t('certificate.fieldConfiguration.userProfileFields.email') },
    { value: 'photo', label: t('certificate.fieldConfiguration.userProfileFields.photo') },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('certificate.fieldConfiguration.title')}</DialogTitle>
          <DialogDescription>
            {t('certificate.fieldConfiguration.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Font Size */}
          <div>
            <Label htmlFor="fontSize">{t('certificate.fieldConfiguration.fontSize')}</Label>
            <Input
              id="fontSize"
              type="number"
              min="8"
              max="72"
              value={fieldConfig.fontSize}
              onChange={(e) => setFieldConfig({ ...fieldConfig, fontSize: Number(e.target.value) })}
            />
          </div>

          {/* Font Family */}
          <div>
            <Label htmlFor="fontFamily">{t('certificate.fieldConfiguration.fontFamily')}</Label>
            <Select
              value={fieldConfig.fontFamily}
              onValueChange={(value) => setFieldConfig({ ...fieldConfig, fontFamily: value })}
            >
              <SelectTrigger id="fontFamily">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_FAMILIES.map((font) => (
                  <SelectItem key={font} value={font}>
                    {font}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Text Style (Bold / Italic / Underline) — only for text-based fields */}
          {mapping.fieldType !== 'QR_CODE' && !(mapping.fieldType === 'USER_PROFILE' && fieldConfig.userProfileField === 'photo') && (
            <div>
              <Label>{t('certificate.fieldConfiguration.textStyle')}</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={fieldConfig.isBold ? 'default' : 'outline'}
                  size="sm"
                  className="w-10 h-9 p-0"
                  onClick={() => setFieldConfig((prev) => ({ ...prev, isBold: !prev.isBold }))}
                  title={t('certificate.fieldConfiguration.bold')}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant={fieldConfig.isItalic ? 'default' : 'outline'}
                  size="sm"
                  className="w-10 h-9 p-0"
                  onClick={() => setFieldConfig((prev) => ({ ...prev, isItalic: !prev.isItalic }))}
                  title={t('certificate.fieldConfiguration.italic')}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant={fieldConfig.isUnderlined ? 'default' : 'outline'}
                  size="sm"
                  className="w-10 h-9 p-0"
                  onClick={() => setFieldConfig((prev) => ({ ...prev, isUnderlined: !prev.isUnderlined }))}
                  title={t('certificate.fieldConfiguration.underline')}
                >
                  <Underline className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Text Transform / Opacity / Shrink to Fit — only for text-based fields */}
          {mapping.fieldType !== 'QR_CODE' && !(mapping.fieldType === 'USER_PROFILE' && fieldConfig.userProfileField === 'photo') && (
            <>
              <div>
                <Label htmlFor="textTransform">{t('certificate.fieldConfiguration.textTransform')}</Label>
                <Select
                  value={fieldConfig.textTransform}
                  onValueChange={(value) => setFieldConfig((prev) => ({ ...prev, textTransform: value }))}
                >
                  <SelectTrigger id="textTransform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('certificate.fieldConfiguration.textTransformOptions.none')}</SelectItem>
                    <SelectItem value="uppercase">{t('certificate.fieldConfiguration.textTransformOptions.uppercase')}</SelectItem>
                    <SelectItem value="lowercase">{t('certificate.fieldConfiguration.textTransformOptions.lowercase')}</SelectItem>
                    <SelectItem value="capitalize">{t('certificate.fieldConfiguration.textTransformOptions.capitalize')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="opacity">
                  {t('certificate.fieldConfiguration.opacity')} ({Math.round(fieldConfig.opacity * 100)}%)
                </Label>
                <input
                  id="opacity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={fieldConfig.opacity}
                  onChange={(e) => setFieldConfig((prev) => ({ ...prev, opacity: Number(e.target.value) }))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer mt-1"
                  style={{
                    background: `linear-gradient(to right, hsl(var(--primary)) ${fieldConfig.opacity * 100}%, #e5e7eb ${fieldConfig.opacity * 100}%)`,
                  }}
                />
              </div>

              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <input
                  id="shrinkToFit"
                  type="checkbox"
                  checked={fieldConfig.shrinkToFit}
                  onChange={(e) => setFieldConfig((prev) => ({ ...prev, shrinkToFit: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                />
                <div>
                  <Label htmlFor="shrinkToFit" className="cursor-pointer font-medium">
                    {t('certificate.fieldConfiguration.shrinkToFit')}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('certificate.fieldConfiguration.shrinkToFitHint')}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Color */}
          <div>
            <Label htmlFor="color">{t('certificate.fieldConfiguration.color')}</Label>
            <div className="flex gap-2">
              <Input
                id="color"
                type="color"
                value={fieldConfig.color}
                onChange={(e) => setFieldConfig({ ...fieldConfig, color: e.target.value })}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={fieldConfig.color}
                onChange={(e) => setFieldConfig({ ...fieldConfig, color: e.target.value })}
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Alignment */}
          <div>
            <Label htmlFor="alignment">{t('certificate.fieldConfiguration.alignment')}</Label>
            <Select
              value={fieldConfig.alignment}
              onValueChange={(value) => setFieldConfig({ ...fieldConfig, alignment: value })}
            >
              <SelectTrigger id="alignment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAlignments(t).map((align) => (
                  <SelectItem key={align.value} value={align.value}>
                    {align.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Label (for display purposes) */}
          <div>
            <Label htmlFor="label">{t('certificate.fieldConfiguration.label')}</Label>
            <Input
              id="label"
              type="text"
              value={fieldConfig.label}
              onChange={(e) => setFieldConfig({ ...fieldConfig, label: e.target.value })}
              placeholder={t('certificate.fieldConfiguration.labelPlaceholder')}
            />
          </div>

          {/* User Profile Field Selection (for USER_PROFILE fields) */}
          {isUserProfileField && (
            <div>
              <Label htmlFor="userProfileField">{t('certificate.fieldConfiguration.userProfileField')}</Label>
              <Select
                value={fieldConfig.userProfileField}
                onValueChange={(value) => setFieldConfig({ ...fieldConfig, userProfileField: value })}
              >
                <SelectTrigger id="userProfileField">
                  <SelectValue placeholder={t('certificate.fieldConfiguration.selectUserProfileField')} />
                </SelectTrigger>
                <SelectContent>
                  {getUserProfileFieldOptions(t).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Form Selection (for CUSTOM fields) */}
          {isCustomField && (
            <>
              <div>
                <Label htmlFor="sourceFormId">{t('certificate.fieldConfiguration.sourceForm')}</Label>
                <Select
                  value={fieldConfig.sourceFormId}
                  onValueChange={(value) => {
                    setFieldConfig({ ...fieldConfig, sourceFormId: value, sourceQuestionId: '' });
                  }}
                >
                  <SelectTrigger id="sourceFormId">
                    <SelectValue placeholder={t('certificate.fieldConfiguration.selectForm')} />
                  </SelectTrigger>
                  <SelectContent>
                    {forms.map((form) => (
                      <SelectItem key={form.id} value={form.id}>
                        {form.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {fieldConfig.sourceFormId && (
                <div>
                  <Label htmlFor="sourceQuestionId">{t('certificate.fieldConfiguration.question')}</Label>
                  <Select
                    value={fieldConfig.sourceQuestionId}
                    onValueChange={(value) => setFieldConfig({ ...fieldConfig, sourceQuestionId: value })}
                    disabled={!fieldConfig.sourceFormId || questions.length === 0}
                  >
                    <SelectTrigger id="sourceQuestionId">
                      <SelectValue placeholder={questions.length === 0 ? t('certificate.fieldConfiguration.loadingQuestions') : t('certificate.fieldConfiguration.selectQuestion')} />
                    </SelectTrigger>
                    <SelectContent>
                      {questions.map((question) => (
                        <SelectItem key={question.id} value={question.id}>
                          {question.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* Width and Height (for QR codes and photo) */}
          {(mapping.fieldType === 'QR_CODE' || (mapping.fieldType === 'USER_PROFILE' && mapping.userProfileField === 'photo')) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="width">{t('certificate.fieldConfiguration.width')}</Label>
                <Input
                  id="width"
                  type="number"
                  min="50"
                  value={mapping.width || 100}
                  onChange={(e) => {
                    const width = Number(e.target.value);
                    if (mapping.fieldType === 'QR_CODE') {
                      onSave({ width, height: width }); // Maintain square aspect ratio for QR codes
                    } else {
                      onSave({ width }); // Allow independent width for photos
                    }
                  }}
                />
              </div>
              <div>
                <Label htmlFor="height">{t('certificate.fieldConfiguration.height')}</Label>
                <Input
                  id="height"
                  type="number"
                  min="50"
                  value={mapping.height || 100}
                  onChange={(e) => {
                    const height = Number(e.target.value);
                    if (mapping.fieldType === 'QR_CODE') {
                      onSave({ width: height, height }); // Maintain square aspect ratio for QR codes
                    } else {
                      onSave({ height }); // Allow independent height for photos
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('certificate.fieldConfiguration.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('certificate.fieldConfiguration.saving')}
              </>
            ) : (
              t('certificate.fieldConfiguration.save')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

